/**
 * EscapeDiscover — `/escape` discovery / browse panel.
 *
 * Founder ruling 18 Feb 2026 PM-5 — separate from Refine. Refine
 * narrows results AFTER a search. Discover helps a member who doesn't
 * yet know what to search.
 *
 * Tabs:
 *   - Top activities         (e.g. "Walking Tours in Paris")
 *   - Top landmarks          (e.g. "Eiffel Tower")
 *   - Explore the world      (region → country → city)
 *   - Categories             (the 14-experience taxonomy)
 *
 * Every chip is a real intent: click → populates the search bar and
 * triggers /api/escape/search with the right query.
 *
 * Visual language: calm Concierge®, NOT a Viator marketplace clone.
 */
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TOP_DESTINATIONS } from "@/components/escape/EscapeTopDestinations";
import EscapeFAQ from "@/components/escape/EscapeFAQ";

const T = {
    ink: "#0a0a0c",
    ink2: "#3a3a42",
    ink3: "#6a6a72",
    line: "#e7e3dc",
    line2: "#d2cdc4",
    card: "#fdfaf3",
    bg: "#f7f3ea",
    brass: "#8a6e2f",
};

const STYLES = {
    label: { fontSize: 11, letterSpacing: "0.24em", textTransform: "uppercase", color: T.ink3, fontFamily: "ui-monospace, monospace" },
};

const TOP_ACTIVITIES = [
    "Walking Tours in Paris", "Historical Tours in Rome",
    "Day Trips in London", "Food Tours in Paris",
    "Wine Tastings in Florence", "Desert Safari in Dubai",
    "Cooking Classes in Tokyo", "Helicopter Tours in New York",
    "Northern Lights Tours in Iceland", "Wildlife Tours in South Africa",
    "Cultural Tours in Barcelona", "Day Trips in Mexico City",
];

const TOP_LANDMARKS = [
    "Eiffel Tower", "Louvre Museum", "Vatican Museums",
    "Colosseum", "Sagrada Família", "Burj Khalifa",
    "Gardens by the Bay", "Statue of Liberty", "Taj Mahal",
    "Great Wall of China", "Mount Fuji", "Grand Canyon",
    "Stonehenge", "Acropolis", "Machu Picchu",
];

const CATEGORIES = [
    "Tours", "Attractions", "Day Trips", "Food & Drink", "Culture",
    "Classes", "Adventure", "Nature & Wildlife", "Water Activities",
    "Family", "Wellness", "Private Tours", "Skip-the-line",
    "Local Experiences", "Shows & Entertainment",
];

const WORLD = [
    {
        region: "Europe",
        countries: [
            { name: "France", cities: ["Paris", "Nice", "Bordeaux", "Lyon"] },
            { name: "Italy", cities: ["Rome", "Florence", "Venice", "Milan", "Naples"] },
            { name: "Spain", cities: ["Barcelona", "Madrid", "Seville", "Granada"] },
            { name: "United Kingdom", cities: ["London", "Edinburgh", "Bath"] },
            { name: "Greece", cities: ["Athens", "Santorini", "Mykonos"] },
            { name: "Iceland", cities: ["Reykjavik"] },
        ],
    },
    {
        region: "Asia",
        countries: [
            { name: "Japan", cities: ["Tokyo", "Kyoto", "Osaka", "Mount Fuji"] },
            { name: "Thailand", cities: ["Bangkok", "Phuket", "Chiang Mai"] },
            { name: "India", cities: ["Delhi", "Mumbai", "Jaipur", "Goa", "Bangalore"] },
            { name: "Indonesia", cities: ["Bali", "Jakarta"] },
            { name: "Singapore", cities: ["Singapore"] },
            { name: "Vietnam", cities: ["Hanoi", "Ho Chi Minh City"] },
        ],
    },
    {
        region: "Middle East",
        countries: [
            { name: "United Arab Emirates", cities: ["Dubai", "Abu Dhabi"] },
            { name: "Saudi Arabia", cities: ["AlUla", "Riyadh"] },
            { name: "Jordan", cities: ["Petra", "Amman"] },
            { name: "Türkiye", cities: ["Istanbul", "Cappadocia"] },
        ],
    },
    {
        region: "Africa",
        countries: [
            { name: "Morocco", cities: ["Marrakech", "Fes"] },
            { name: "Egypt", cities: ["Cairo", "Luxor"] },
            { name: "South Africa", cities: ["Cape Town", "Johannesburg"] },
            { name: "Kenya", cities: ["Nairobi", "Maasai Mara"] },
            { name: "Tanzania", cities: ["Serengeti", "Zanzibar"] },
        ],
    },
    {
        region: "North America",
        countries: [
            { name: "United States", cities: ["New York", "Las Vegas", "San Francisco", "Los Angeles"] },
            { name: "Canada", cities: ["Toronto", "Vancouver", "Banff"] },
        ],
    },
    {
        region: "Central & South America",
        countries: [
            { name: "Peru", cities: ["Cusco", "Lima", "Machu Picchu"] },
            { name: "Mexico", cities: ["Mexico City", "Cancun", "Tulum"] },
            { name: "Argentina", cities: ["Buenos Aires", "Patagonia"] },
            { name: "Chile", cities: ["Santiago", "Atacama"] },
            { name: "Brazil", cities: ["Rio de Janeiro", "São Paulo"] },
        ],
    },
    {
        region: "Australia & Pacific",
        countries: [
            { name: "Australia", cities: ["Sydney", "Melbourne", "Cairns"] },
            { name: "New Zealand", cities: ["Auckland", "Queenstown"] },
            { name: "French Polynesia", cities: ["Bora Bora", "Tahiti"] },
        ],
    },
    {
        region: "Caribbean",
        countries: [
            { name: "Bahamas", cities: ["Nassau"] },
            { name: "Jamaica", cities: ["Montego Bay"] },
            { name: "Barbados", cities: ["Bridgetown"] },
        ],
    },
];

const Chip = ({ children, onClick, testid }) => (
    <button
        type="button"
        data-testid={testid}
        onClick={onClick}
        style={{
            background: T.card, border: `1px solid ${T.line2}`,
            borderRadius: 999, padding: "8px 14px", fontSize: 13,
            color: T.ink2, cursor: "pointer", fontFamily: "inherit",
            textAlign: "left",
        }}
        onMouseEnter={(e) => { e.currentTarget.style.borderColor = T.brass; }}
        onMouseLeave={(e) => { e.currentTarget.style.borderColor = T.line2; }}
    >
        {children}
    </button>
);

export default function EscapeDiscover({ open, onClose, onPick }) {
    const [tab, setTab] = useState("activities");
    const [openRegion, setOpenRegion] = useState("Europe");
    const navigate = useNavigate();
    if (!open) return null;

    const tabs = [
        { id: "activities",   label: "Top activities" },
        { id: "landmarks",    label: "Top landmarks" },
        { id: "world",        label: "Explore the world" },
        { id: "categories",   label: "Categories" },
        { id: "destinations", label: "Destinations" },
        { id: "faqs",         label: "FAQs" },
    ];

    function pick(query) {
        onPick?.(query);
        onClose?.();
    }

    function goToDestination(slug) {
        onClose?.();
        navigate(`/escape/destination/${slug}`);
    }

    return (
        <div
            role="dialog"
            aria-label="Browse escapes"
            data-testid="escape-discover-drawer"
            style={{
                position: "fixed", inset: 0, zIndex: 55,
                background: "rgba(10,10,12,0.4)",
                display: "flex", justifyContent: "center", alignItems: "flex-start",
                padding: "40px 22px", overflowY: "auto",
            }}
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
        >
            <div style={{
                width: "min(960px, 100%)",
                maxHeight: "calc(100vh - 80px)", overflowY: "auto",
                background: T.bg, borderRadius: 18,
                border: `1px solid ${T.line2}`,
            }}>
                <div style={{ position: "sticky", top: 0, background: T.bg, zIndex: 2, padding: "20px 28px 12px", borderBottom: `1px solid ${T.line}` }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
                        <div>
                            <div style={{ ...STYLES.label, color: T.brass, marginBottom: 6 }}>Browse escapes</div>
                            <div className="t-serif" style={{ fontSize: 22, color: T.ink }}>Selected paths to begin</div>
                        </div>
                        <button
                            data-testid="escape-discover-close"
                            onClick={onClose}
                            style={{ background: T.card, border: `1px solid ${T.line2}`, borderRadius: 999, width: 36, height: 36, fontSize: 20, color: T.ink2, cursor: "pointer" }}
                        >×</button>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                        {tabs.map((t) => (
                            <button
                                key={t.id}
                                data-testid={`escape-discover-tab-${t.id}`}
                                onClick={() => setTab(t.id)}
                                style={{
                                    background: tab === t.id ? T.ink : "transparent",
                                    color: tab === t.id ? T.card : T.ink2,
                                    border: `1px solid ${tab === t.id ? T.ink : T.line2}`,
                                    borderRadius: 999, padding: "8px 16px",
                                    fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                                }}
                            >
                                {t.label}
                            </button>
                        ))}
                    </div>
                </div>

                <div style={{ padding: "20px 28px 40px" }}>
                    {tab === "activities" && (
                        <div data-testid="escape-discover-activities">
                            <p style={{ fontSize: 13, color: T.ink3, margin: "0 0 16px" }}>
                                Things to do, beautifully sorted. Tap any to begin.
                            </p>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 10 }}>
                                {TOP_ACTIVITIES.map((a) => (
                                    <Chip
                                        key={a}
                                        testid={`escape-discover-activity-${a.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                                        onClick={() => pick(a)}
                                    >{a}</Chip>
                                ))}
                            </div>
                        </div>
                    )}

                    {tab === "landmarks" && (
                        <div data-testid="escape-discover-landmarks">
                            <p style={{ fontSize: 13, color: T.ink3, margin: "0 0 16px" }}>
                                Landmark-led routes. The story is already there — Concierge® opens the door.
                            </p>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 10 }}>
                                {TOP_LANDMARKS.map((l) => (
                                    <Chip
                                        key={l}
                                        testid={`escape-discover-landmark-${l.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                                        onClick={() => pick(l)}
                                    >{l}</Chip>
                                ))}
                            </div>
                        </div>
                    )}

                    {tab === "world" && (
                        <div data-testid="escape-discover-world">
                            <p style={{ fontSize: 13, color: T.ink3, margin: "0 0 16px" }}>
                                Choose a region. Drill down to country and city.
                            </p>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 20 }}>
                                {WORLD.map((r) => (
                                    <button
                                        key={r.region}
                                        data-testid={`escape-discover-region-${r.region.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                                        onClick={() => setOpenRegion(r.region)}
                                        style={{
                                            background: openRegion === r.region ? T.ink : "transparent",
                                            color: openRegion === r.region ? T.card : T.ink2,
                                            border: `1px solid ${openRegion === r.region ? T.ink : T.line2}`,
                                            borderRadius: 999, padding: "7px 14px",
                                            fontSize: 13, cursor: "pointer", fontFamily: "inherit",
                                        }}
                                    >
                                        {r.region}
                                    </button>
                                ))}
                            </div>
                            {WORLD.filter((r) => r.region === openRegion).map((r) => (
                                <div key={r.region} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 18 }}>
                                    {r.countries.map((c) => (
                                        <div key={c.name}>
                                            <div className="t-serif" style={{ fontSize: 16, color: T.ink, marginBottom: 8 }}>{c.name}</div>
                                            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                                                {c.cities.map((city) => (
                                                    <Chip
                                                        key={city}
                                                        testid={`escape-discover-city-${city.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                                                        onClick={() => pick(city)}
                                                    >{city}</Chip>
                                                ))}
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}

                    {tab === "categories" && (
                        <div data-testid="escape-discover-categories">
                            <p style={{ fontSize: 13, color: T.ink3, margin: "0 0 16px" }}>
                                The fourteen ways /escape moves. Pick one to see live experiences.
                            </p>
                            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: 10 }}>
                                {CATEGORIES.map((c) => (
                                    <Chip
                                        key={c}
                                        testid={`escape-discover-cat-${c.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                                        onClick={() => pick(c)}
                                    >{c}</Chip>
                                ))}
                            </div>
                        </div>
                    )}

                    {tab === "destinations" && (
                        <div data-testid="escape-discover-destinations">
                            <p style={{ fontSize: 13, color: T.ink3, margin: "0 0 16px" }}>
                                Curated destination pages · live Viator results, calmed by the Concierge®.
                            </p>
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
                                gap: 14,
                            }}>
                                {TOP_DESTINATIONS.map((d) => (
                                    <button
                                        key={d.slug}
                                        type="button"
                                        data-testid={`escape-discover-destination-${d.slug}`}
                                        onClick={() => goToDestination(d.slug)}
                                        style={{
                                            background: "transparent",
                                            border: 0, padding: 0,
                                            cursor: "pointer", textAlign: "left",
                                            fontFamily: "inherit",
                                        }}
                                    >
                                        <div style={{
                                            width: "100%", aspectRatio: "4/3",
                                            borderRadius: 12,
                                            border: `1px solid ${T.line}`,
                                            position: "relative", overflow: "hidden",
                                            background: `linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.55)), url(${d.image}) center/cover no-repeat`,
                                        }}>
                                            <div style={{
                                                position: "absolute", bottom: 10, left: 12, right: 12,
                                                color: "#fff",
                                            }}>
                                                <div className="t-serif" style={{ fontSize: 17, lineHeight: 1.15 }}>{d.name}</div>
                                                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.82)", marginTop: 2 }}>{d.sub}</div>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {tab === "faqs" && (
                        <div data-testid="escape-discover-faqs">
                            <p style={{ fontSize: 13, color: T.ink3, margin: "0 0 16px" }}>
                                Common questions members ask before booking with /escape.
                            </p>
                            <div data-testid="escape-discover-faq-embed">
                                <EscapeFAQ embedded />
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
